/**
 * MobileBottomNav - Mobile navigation bar and "More" menu
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Map, Lightbulb, Users, DollarSign, MoreHorizontal,
  History, Trophy, MessageSquare, Plane, Binoculars, Crown, User,
  Book, Sparkles, CalendarHeart, Download, Globe, Gift, Film
} from "lucide-react";
import { API } from "@/App";

export const MobileBottomNav = ({
  user,
  onShowWorldMap,
  onShowSuggestions,
  onShowGroups,
  onShowBudget,
  onShowPastTrips,
  onShowAchievements,
  onShowTravelSearch,
  onShowExplore,
  onShowScrapbook,
  onShowMemories,
  onShowReminders,
  onShowExport,
  onShowReferrals,
  onShowYearInReview,
}) => {
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Determine avatar URL
  const avatarUrl = user?.profile_picture_path 
    ? `${API}/profile/picture/${user.profile_picture_path}`
    : user?.preferences?.avatar_url || null;

  // Hide web bottom nav ONLY when the new hybrid native shell is rendering this WebView
  // (the new shell provides its own bottom tab bar). The legacy WebView-only build
  // sets `isNativeApp` but has NO native tabs, so we must keep showing the web nav there.
  const isHybridNativeShell = typeof window !== 'undefined' && window.duolystNativeShell === true;
  if (isHybridNativeShell) return null;

  return (
    <>
      {/* Bottom Navigation - show on all screens below lg (1024px) */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden z-[9999]" 
        data-testid="mobile-nav"
      >
        <div className="flex justify-around items-center px-4 py-3 max-w-md mx-auto">
          <MobileNavItem icon={Map} label="Map" onClick={onShowWorldMap} testId="mobile-nav-map" />
          <MobileNavItem icon={Lightbulb} label="Ideas" onClick={onShowSuggestions} testId="mobile-nav-suggestions" />
          <MobileNavItem icon={Trophy} label="Badges" onClick={onShowAchievements} testId="mobile-nav-achievements" />
          <MobileNavItem icon={Users} label="Groups" onClick={onShowGroups} testId="mobile-nav-groups" />
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className={`flex flex-col items-center px-3 py-1 active:scale-95 transition-transform ${showMobileMenu ? 'text-primary' : 'text-foreground/70 hover:text-primary'}`}
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal className="w-7 h-7" />
            <span className="text-xs mt-1 font-medium">More</span>
          </button>
        </div>
      </div>

      {/* More Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[9998] lg:hidden" onClick={() => setShowMobileMenu(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div 
            className="absolute bottom-20 left-4 right-4 bg-card rounded-2xl shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border sticky top-0 bg-card">
              <h3 className="font-semibold text-foreground">More Features</h3>
            </div>
            <div className="py-2">
              {/* Memories Section */}
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Memories
              </div>
              <MobileMenuItem 
                icon={Sparkles} 
                label="Memories Timeline" 
                iconClass="text-pink-500"
                onClick={() => { setShowMobileMenu(false); onShowMemories?.(); }} 
              />
              <MobileMenuItem 
                icon={Book} 
                label="Scrapbook" 
                iconClass="text-amber-600"
                onClick={() => { setShowMobileMenu(false); onShowScrapbook?.(); }} 
              />
              <MobileMenuItem 
                icon={History} 
                label="Log Past Trips" 
                iconClass="text-secondary"
                onClick={() => { setShowMobileMenu(false); onShowPastTrips(); }} 
              />
              <MobileMenuItem
                icon={Film}
                label="Year in Review (MP4)"
                iconClass="text-pink-500"
                onClick={() => { setShowMobileMenu(false); onShowYearInReview?.(); }}
              />
              
              {/* Planning Section */}
              <div className="px-4 py-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-t border-border">
                Planning
              </div>
              <MobileMenuItem 
                icon={CalendarHeart} 
                label="Reminders & Anniversaries" 
                iconClass="text-rose-500"
                onClick={() => { setShowMobileMenu(false); onShowReminders?.(); }} 
              />
              <MobileMenuItem 
                icon={DollarSign} 
                label="Budget Tracker" 
                iconClass="text-emerald-600"
                onClick={() => { setShowMobileMenu(false); onShowBudget(); }} 
              />
              <MobileMenuItem 
                icon={Plane} 
                label="Travel Search" 
                iconClass="text-primary"
                onClick={() => { setShowMobileMenu(false); onShowTravelSearch(); }} 
              />
              <MobileMenuItem 
                icon={Binoculars} 
                label="Explore Destinations" 
                iconClass="text-secondary"
                onClick={() => { setShowMobileMenu(false); onShowExplore(); }} 
              />
              
              {/* Premium Section */}
              <div className="px-4 py-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-t border-border">
                Premium
              </div>
              <MobileMenuItem 
                icon={Crown} 
                label="Upgrade to Pro" 
                iconClass="text-amber-500"
                onClick={() => { setShowMobileMenu(false); navigate("/pricing"); }} 
              />
              <MobileMenuItem 
                icon={Gift} 
                label="Invite & Earn Rewards" 
                iconClass="text-pink-500"
                onClick={() => { setShowMobileMenu(false); onShowReferrals?.(); }} 
              />
              
              {/* More Section */}
              <div className="px-4 py-2 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-t border-border">
                Community
              </div>
              <MobileMenuItem 
                icon={Globe} 
                label="Explore Community" 
                iconClass="text-primary"
                onClick={() => { setShowMobileMenu(false); navigate("/explore"); }} 
              />
              <MobileMenuItem 
                icon={Download} 
                label="Export & Share" 
                iconClass="text-indigo-500"
                onClick={() => { setShowMobileMenu(false); onShowExport?.(); }} 
              />
              <MobileMenuItem 
                icon={MessageSquare} 
                label="Feedback Board" 
                iconClass="text-secondary"
                onClick={() => { setShowMobileMenu(false); navigate("/feedback"); }} 
              />
              <MobileMenuItem 
                icon={Trophy} 
                label="Hall of Fame" 
                iconClass="text-yellow-500"
                onClick={() => { setShowMobileMenu(false); navigate("/hall-of-fame"); }} 
              />
              <MobileMenuItem 
                icon={User} 
                label="Profile & Settings" 
                iconClass="text-charcoal"
                avatarUrl={avatarUrl}
                userName={user?.name}
                onClick={() => { setShowMobileMenu(false); navigate("/profile"); }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Spacer for bottom nav */}
      <div className="h-16 lg:hidden" />
    </>
  );
};

const MobileNavItem = ({ icon: Icon, label, onClick, testId }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center px-3 py-1 text-foreground/70 hover:text-primary active:scale-95 transition-transform"
    data-testid={testId}
  >
    <Icon className="w-7 h-7" />
    <span className="text-xs mt-1 font-medium">{label}</span>
  </button>
);

const MobileMenuItem = ({ icon: Icon, label, iconClass, onClick, avatarUrl, userName }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted text-left"
  >
    {avatarUrl ? (
      <img 
        src={avatarUrl} 
        alt={userName || 'Profile'} 
        className="w-5 h-5 rounded-full object-cover"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'block';
        }}
      />
    ) : null}
    <Icon className={`w-5 h-5 ${iconClass} ${avatarUrl ? 'hidden' : ''}`} />
    <span className="font-medium text-foreground">{label}</span>
  </button>
);

export default MobileBottomNav;
